// app/javascript/controllers/image_crop_controller.js
//
// Ritaglio immagine con Cropper.js prima dell'upload, su qualunque
// <input type="file"> per immagini.
//
// Due modalità (data-image-crop-mode-value):
//   - "modal": per form standalone (es. pagina Nuovo/Modifica Articolo).
//     Apre una modale Bootstrap temporanea, creata a runtime e agganciata
//     a <body> (necessario per un corretto posizionamento/z-index, dato
//     che il form potrebbe già trovarsi dentro card/tabelle con overflow).
//   - "panel": per form che si trovano già dentro una modale Bootstrap
//     (es. gallerie foto, anagrafica foto). Non si può aprire una
//     seconda modale sopra la prima: si espande invece un pannello
//     inline subito sotto il campo file, dentro la modale corrente.
//
// Formato di ritaglio: l'utente può scegliere Verticale/Orizzontale/
// Quadrato/Libero tramite i pulsanti mostrati sopra l'immagine.
// data-image-crop-default-shape-value imposta il formato preselezionato
// (resta comunque modificabile dall'utente).
//
// Una volta confermato il ritaglio non si mostra più un thumbnail sotto
// il campo file: accanto alla label del campo compaiono un'icona
// (apre un popup con l'anteprima della foto ritagliata) e un checkbox
// con icona cestino per annullare la selezione appena fatta, sullo
// stesso modello già usato per le foto già salvate (image_modal_button
// + checkbox "rimuovi"). Dove esiste anche un checkbox "Verificata"
// (target "verifiedWrapper", già presente nel markup ma nascosto se non
// c'è ancora una foto salvata) viene reso visibile anch'esso alla
// conferma del ritaglio, e ri-nascosto se l'utente annulla la nuova
// selezione — a meno che non fosse già visibile per una foto salvata
// in precedenza (data-original-visible="true").
//
// Uso:
//   <div data-controller="image-crop"
//        data-image-crop-mode-value="modal"
//        data-image-crop-default-shape-value="verticale">
//     <div class="d-flex align-items-center mb-1">
//       <%= f.label :photo %>
//       <button data-image-crop-target="previewButton" data-action="click->image-crop#showPreview" class="d-none">...</button>
//       <span data-image-crop-target="removeWrapper" class="d-none">
//         <input type="checkbox" data-image-crop-target="removeCheckbox" data-action="change->image-crop#cancelPending">
//       </span>
//     </div>
//     <%= f.file_field :photo, data: { image_crop_target: "input", action: "change->image-crop#fileSelected" } %>
//   </div>

import { Controller } from "@hotwired/stimulus"
import Cropper from "cropperjs"
import * as bootstrap from "bootstrap"

const SHAPES = [
  { key: "verticale", label: "Verticale", ratio: 3 / 4 },
  { key: "orizzontale", label: "Orizzontale", ratio: 4 / 3 },
  { key: "quadrato", label: "Quadrato", ratio: 1 },
  { key: "libero", label: "Libero", ratio: NaN }
]

export default class extends Controller {
  static targets = ["input", "previewButton", "removeWrapper", "removeCheckbox", "verifiedWrapper"]
  static values = {
    mode: { type: String, default: "modal" },
    defaultShape: { type: String, default: "libero" }
  }

  connect() {
    this.cropper = null
    this.container = null
    this.bsModal = null
    this.croppedDataUrl = null
  }

  disconnect() {
    this.teardownUI()
  }

  fileSelected(event) {
    const file = event.target.files && event.target.files[0]
    if (!file) return

    if (!file.type || !file.type.startsWith("image/")) {
      // Non è un'immagine (non dovrebbe capitare su questi campi): lascia
      // che il form gestisca il file così com'è.
      return
    }

    if (file.type === "image/gif" || file.type === "image/svg+xml") {
      // Il ritaglio rasterizza sempre il risultato in JPEG statico: su
      // una GIF farebbe perdere l'animazione, su un SVG la natura
      // vettoriale. Questi due formati vengono caricati così come sono,
      // senza passare dal ritaglio.
      return
    }

    this.currentFilename = file.name

    const reader = new FileReader()
    reader.onload = (e) => this.openCropper(e.target.result)
    reader.readAsDataURL(file)
  }

  openCropper(dataUrl) {
    this.teardownUI() // nel caso fosse rimasta aperta un'istanza precedente

    this.container = this.buildContainer()
    this.imageEl = this.container.querySelector("[data-role='image']")
    this.imageEl.src = dataUrl

    this.wireButtons(this.container)

    if (this.modeValue === "panel") {
      this.element.appendChild(this.container)
      this.container.classList.remove("d-none")
      this.initCropper()
    } else {
      document.body.appendChild(this.container)
      this.bsModal = new bootstrap.Modal(this.container)
      this.container.addEventListener("shown.bs.modal", () => this.initCropper(), { once: true })
      this.container.addEventListener("hidden.bs.modal", () => this.teardownUI(), { once: true })
      this.bsModal.show()
    }
  }

  initCropper() {
    const options = {
      viewMode: 1,
      autoCropArea: 1,
      background: false,
      responsive: true
    }
    const ratio = this.shapeRatio(this.defaultShapeValue)
    if (!Number.isNaN(ratio)) options.aspectRatio = ratio
    this.cropper = new Cropper(this.imageEl, options)
  }

  rotateLeft() { this.cropper?.rotate(-90) }
  rotateRight() { this.cropper?.rotate(90) }
  zoomIn() { this.cropper?.zoom(0.1) }
  zoomOut() { this.cropper?.zoom(-0.1) }
  resetCrop() { this.cropper?.reset() }

  shapeRatio(shape) {
    return (SHAPES.find(s => s.key === shape) || {}).ratio ?? NaN
  }

  setShape(button, container) {
    const shape = button.dataset.role.replace("shape-", "")
    this.cropper?.setAspectRatio(this.shapeRatio(shape))
    container.querySelectorAll("[data-role^='shape-']").forEach(b => b.classList.remove("active"))
    button.classList.add("active")
  }

  confirm() {
    if (!this.cropper) return

    const canvas = this.cropper.getCroppedCanvas({ imageSmoothingQuality: "high" })
    if (!canvas) return

    canvas.toBlob((blob) => {
      if (!blob) return

      const croppedFile = new File([blob], this.currentFilename, { type: "image/jpeg" })
      const dataTransfer = new DataTransfer()
      dataTransfer.items.add(croppedFile)
      this.inputTarget.files = dataTransfer.files

      this.croppedDataUrl = canvas.toDataURL("image/jpeg", 0.92)

      if (this.hasPreviewButtonTarget) this.previewButtonTarget.classList.remove("d-none")
      if (this.hasRemoveWrapperTarget) this.removeWrapperTarget.classList.remove("d-none")
      if (this.hasRemoveCheckboxTarget) this.removeCheckboxTarget.checked = false
      if (this.hasVerifiedWrapperTarget) this.verifiedWrapperTarget.classList.remove("d-none")

      this.closeUI()
    }, "image/jpeg", 0.92)
  }

  cancel() {
    this.inputTarget.value = ""
    this.closeUI()
  }

  // Overlay realizzato "a mano" (non bootstrap.Modal) perché il pannello
  // ritaglio in modalità "panel" vive già dentro una modale Bootstrap: una
  // seconda istanza di bootstrap.Modal impilata sopra confonde il conteggio
  // interno di backdrop/`modal-open` di Bootstrap e, chiudendo l'anteprima,
  // sparisce anche lo sfondo scurito della modale sottostante.
  showPreview(event) {
    if (event) event.preventDefault()
    if (!this.croppedDataUrl) return

    const overlay = this.buildPreviewOverlay()
    document.body.appendChild(overlay)

    const close = () => {
      overlay.remove()
      document.removeEventListener("keydown", onKeydown)
    }
    const onKeydown = (e) => { if (e.key === "Escape") close() }

    overlay.addEventListener("click", (e) => { if (e.target === overlay) close() })
    overlay.querySelectorAll("[data-role='close-preview']").forEach(b => b.addEventListener("click", close))
    document.addEventListener("keydown", onKeydown)
  }

  cancelPending(event) {
    if (!event.target.checked) return
    this.clearPending()
  }

  clearPending() {
    this.inputTarget.value = ""
    this.croppedDataUrl = null
    if (this.hasPreviewButtonTarget) this.previewButtonTarget.classList.add("d-none")
    if (this.hasRemoveWrapperTarget) this.removeWrapperTarget.classList.add("d-none")
    if (this.hasRemoveCheckboxTarget) this.removeCheckboxTarget.checked = false

    // Il checkbox "Verificata" resta visibile se già mostrato per una foto
    // salvata in precedenza: annullando la nuova selezione si torna solo
    // allo stato di quella foto già esistente, non va nascosto.
    if (this.hasVerifiedWrapperTarget && this.verifiedWrapperTarget.dataset.originalVisible !== "true") {
      this.verifiedWrapperTarget.classList.add("d-none")
    }
  }

  closeUI() {
    if (this.modeValue === "panel") {
      this.teardownUI()
    } else if (this.bsModal) {
      this.bsModal.hide() // il cleanup avviene su "hidden.bs.modal"
    }
  }

  teardownUI() {
    if (this.cropper) {
      this.cropper.destroy()
      this.cropper = null
    }
    if (this.bsModal) {
      this.bsModal.dispose()
      this.bsModal = null
    }
    if (this.container) {
      this.container.remove()
      this.container = null
    }
  }

  wireButtons(container) {
    container.querySelectorAll("[data-role='rotate-left']").forEach(b => b.addEventListener("click", () => this.rotateLeft()))
    container.querySelectorAll("[data-role='rotate-right']").forEach(b => b.addEventListener("click", () => this.rotateRight()))
    container.querySelectorAll("[data-role='zoom-in']").forEach(b => b.addEventListener("click", () => this.zoomIn()))
    container.querySelectorAll("[data-role='zoom-out']").forEach(b => b.addEventListener("click", () => this.zoomOut()))
    container.querySelectorAll("[data-role='reset']").forEach(b => b.addEventListener("click", () => this.resetCrop()))
    container.querySelectorAll("[data-role='confirm']").forEach(b => b.addEventListener("click", () => this.confirm()))
    container.querySelectorAll("[data-role='cancel']").forEach(b => b.addEventListener("click", () => this.cancel()))
    container.querySelectorAll("[data-role^='shape-']").forEach(b => b.addEventListener("click", () => this.setShape(b, container)))
  }

  toolbarButtonsHtml() {
    return `
      <button type="button" class="btn btn-outline-secondary" data-role="rotate-left" title="Ruota a sinistra"><i class="fas fa-rotate-left"></i></button>
      <button type="button" class="btn btn-outline-secondary" data-role="rotate-right" title="Ruota a destra"><i class="fas fa-rotate-right"></i></button>
      <button type="button" class="btn btn-outline-secondary" data-role="zoom-in" title="Zoom avanti"><i class="fas fa-magnifying-glass-plus"></i></button>
      <button type="button" class="btn btn-outline-secondary" data-role="zoom-out" title="Zoom indietro"><i class="fas fa-magnifying-glass-minus"></i></button>
      <button type="button" class="btn btn-outline-secondary" data-role="reset" title="Reimposta"><i class="fas fa-arrows-rotate"></i></button>
    `
  }

  shapeButtonsHtml() {
    const buttons = SHAPES.map(shape => {
      const active = shape.key === this.defaultShapeValue ? " active" : ""
      return `<button type="button" class="btn btn-outline-secondary${active}" data-role="shape-${shape.key}">${shape.label}</button>`
    }).join("")

    return `
      <div class="btn-group btn-group-sm" role="group" aria-label="Formato ritaglio">
        ${buttons}
      </div>
    `
  }

  buildContainer() {
    const div = document.createElement("div")

    if (this.modeValue === "panel") {
      div.className = "card mt-2 d-none image-crop-panel"
      div.innerHTML = `
        <div class="card-header d-flex justify-content-between align-items-center py-2">
          <span><i class="fas fa-crop-simple me-1"></i> Ritaglia immagine</span>
          <button type="button" class="btn-close" data-role="cancel" aria-label="Annulla"></button>
        </div>
        <div class="px-3 py-2 border-bottom bg-white d-flex flex-wrap justify-content-center">
          ${this.shapeButtonsHtml()}
        </div>
        <div class="card-body text-center bg-light" style="max-height: 45vh; overflow: hidden;">
          <img data-role="image" style="max-width: 100%; display: block;">
        </div>
        <div class="card-footer d-flex flex-wrap justify-content-end gap-2">
          ${this.toolbarButtonsHtml()}
          <button type="button" class="btn btn-secondary" data-role="cancel">Annulla</button>
          <button type="button" class="btn btn-primary" data-role="confirm"><i class="fas fa-check me-1"></i>Applica ritaglio</button>
        </div>
      `
    } else {
      div.className = "modal fade"
      div.tabIndex = -1
      div.innerHTML = `
        <div class="modal-dialog modal-lg modal-dialog-centered">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title"><i class="fas fa-crop-simple me-1"></i> Ritaglia immagine</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" data-role="cancel" aria-label="Annulla"></button>
            </div>
            <div class="px-3 py-2 border-bottom bg-white d-flex flex-wrap justify-content-center">
              ${this.shapeButtonsHtml()}
            </div>
            <div class="modal-body text-center bg-light" style="max-height: 60vh; overflow: hidden;">
              <img data-role="image" style="max-width: 100%; display: block;">
            </div>
            <div class="modal-footer d-flex flex-wrap justify-content-end gap-2">
              ${this.toolbarButtonsHtml()}
              <button type="button" class="btn btn-secondary" data-role="cancel">Annulla</button>
              <button type="button" class="btn btn-primary" data-role="confirm"><i class="fas fa-check me-1"></i>Applica ritaglio</button>
            </div>
          </div>
        </div>
      `
    }

    return div
  }

  buildPreviewOverlay() {
    const div = document.createElement("div")
    div.className = "image-crop-preview-overlay"
    div.style.cssText = "position:fixed;inset:0;z-index:1090;background:rgba(0,0,0,.5);display:flex;align-items:center;justify-content:center;padding:1rem;"
    div.innerHTML = `
      <div class="bg-secondary border-0 rounded shadow" style="max-width:800px;width:100%;">
        <div class="d-flex justify-content-between align-items-center p-3 bg-warning rounded-top">
          <h5 class="modal-title mb-0">Anteprima foto</h5>
          <button type="button" class="btn-close" data-role="close-preview" aria-label="Chiudi"></button>
        </div>
        <div class="bg-white text-center p-3">
          <img src="${this.croppedDataUrl}" class="img-fluid rounded shadow" style="max-height:70vh;">
        </div>
        <div class="bg-dark text-white d-flex justify-content-end p-2 rounded-bottom">
          <button type="button" class="btn btn-info" data-role="close-preview">Chiudi</button>
        </div>
      </div>
    `
    return div
  }
}
